import { Link } from "react-router-dom";
import { Heart } from "lucide-react";
import { isFavorite, toggleFavorite } from "@/lib/api";
import { useState } from "react";

/**
 * Editorial SareeCard — no chunky containing box, just an art-gallery image
 * frame with minimalist text below. Two-image hover swap for premium feel.
 */
export default function SareeCard({ saree, shopSlug }) {
    const [fav, setFav] = useState(isFavorite(saree.id));
    const onFav = (e) => {
        e.preventDefault();
        e.stopPropagation();
        toggleFavorite({ ...saree, shop_slug: shopSlug });
        setFav(!fav);
    };
    const hero = saree.ai_model_images?.[0] || saree.images?.[0];
    const hover = saree.ai_model_images?.[1] || saree.ai_model_images?.[0];
    // Backend sets `auto_price` on jewellery items from the shop's current rate.
    // Prefer the manually-set `price` if it exists (>0), else fall back to auto.
    const numericPrice = Number(saree.price) > 0 ? Number(saree.price) : Number(saree.auto_price || 0);
    const price = numericPrice > 0 ? numericPrice.toLocaleString("en-IN") : null;
    const isAuto = !Number(saree.price) && Number(saree.auto_price) > 0;
    const original = saree.original_price && saree.original_price > numericPrice
        ? Number(saree.original_price).toLocaleString("en-IN")
        : null;

    return (
        <Link
            to={`/shop/${shopSlug}/saree/${saree.id}`}
            className="group block"
            data-testid={`saree-card-${saree.id}`}
        >
            {/* Image frame */}
            <div className="editorial-frame aspect-[3/4] bg-brand-subtle">
                {hero ? (
                    <img
                        src={hero}
                        alt={saree.title}
                        loading="lazy"
                        decoding="async"
                        className="absolute inset-0 w-full h-full object-cover"
                    />
                ) : (
                    <div className="absolute inset-0 grid place-items-center text-brand-soft text-xs">No image</div>
                )}
                {hover && hover !== hero ? (
                    <img
                        src={hover}
                        alt=""
                        loading="lazy"
                        decoding="async"
                        className="absolute inset-0 w-full h-full object-cover opacity-0 group-hover:opacity-100 transition-opacity duration-700"
                    />
                ) : null}

                {/* badges */}
                {saree.is_trending ? (
                    <span className="absolute top-3 left-3 z-10 font-mono text-[10px] uppercase tracking-wider-2 bg-brand-paper px-2.5 py-1 text-brand-ink">
                        Trending
                    </span>
                ) : null}
                {saree.offer ? (
                    <span className="absolute top-3 right-3 z-10 font-mono text-[10px] uppercase tracking-wider-2 bg-brand-ink text-white px-2.5 py-1">
                        {saree.offer}
                    </span>
                ) : null}

                {/* favorite button */}
                <button
                    onClick={onFav}
                    className="absolute bottom-3 right-3 z-10 w-9 h-9 grid place-items-center bg-brand-paper/95 backdrop-blur hover:bg-brand-paper transition rounded-full"
                    aria-label="Favorite"
                    data-testid={`fav-btn-${saree.id}`}
                >
                    <Heart
                        className={`w-4 h-4 transition-colors ${fav ? "fill-brand-bronze text-brand-bronze" : "text-brand-ink"}`}
                        strokeWidth={1.5}
                    />
                </button>
            </div>

            {/* meta — left = name, right = price */}
            <div className="mt-3 flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                    {(saree.purity || saree.brand || saree.fabric) ? (
                        <div className="ayraa-overline truncate">{saree.purity || saree.brand || saree.fabric}</div>
                    ) : null}
                    <div className="font-display text-base sm:text-lg leading-snug mt-0.5 line-clamp-1 tracking-editorial">
                        {saree.title}
                    </div>
                    {saree.net_weight_g ? (
                        <div className="text-[10px] font-mono uppercase tracking-wider-2 text-brand-soft mt-0.5">
                            {saree.net_weight_g}g{saree.huid ? ` · ${saree.huid}` : ""}
                        </div>
                    ) : null}
                </div>
                <div className="text-right shrink-0">
                    {price ? (
                        <>
                            <div className="font-mono text-sm font-medium text-brand-ink">₹{price}</div>
                            {isAuto ? (
                                <div className="font-mono text-[9px] uppercase tracking-wider-2 text-brand-bronze mt-0.5">today&apos;s rate</div>
                            ) : null}
                            {original ? (
                                <div className="font-mono text-[11px] text-brand-soft line-through mt-0.5">₹{original}</div>
                            ) : null}
                        </>
                    ) : (
                        <span className="text-brand-muted text-xs">On request</span>
                    )}
                </div>
            </div>
        </Link>
    );
}
